/**
 * YoutubeCorrelationCard — Convergence / NMW-282 (plan §11.3/§11.4).
 *
 * The frontend consumer of the per-channel correlation read-model
 * `ytCorrelation.getYoutubeChannelCorrelation` — a read-only join of the LIVE
 * health plane and the two latency-tagged earnings figures. Channel-scoped (the
 * query takes a channel id), so it mounts on the channel detail Health tab.
 *
 * Honest-state contract (matches the insights bundle idiom):
 *   - query === undefined -> skeleton
 *   - query === null (channel does not resolve) -> empty state
 *   - data -> latency-labeled cards
 *
 * NMW-457: the "Atribución por ruta (cortes observados)" panel was removed. Its
 * flip counters were structurally always 0 — `convex/crons/adBreakClassifier.ts`
 * only reaches `flip_observed` when `ytStatus200Count > 0`, and the only write
 * of that field in the repo is the literal `ytStatus200Count: 0` initializer in
 * `convex/adBreaks.ts` (the YouTube 200-status probe is not deployed). The query
 * still returns `correlation`; this card simply does not render it.
 */

import React from 'react';
import {
  Panel, PageHead, StatCard, EmptyState, SkeletonStatRow, SkeletonPanel,
} from '../../components/primitives';
import { Provenance } from '../../components/Provenance';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';

type Correlation = {
  channelId: string;
  rangeDays: number;
  liveViewers: {
    latency: 'realtime';
    youtubeStreamStatus: 'live' | 'none' | 'upcoming' | 'unknown' | 'error' | null;
    concurrentViewers: number | null;
    ingestHealth: string | null;
    ingestStreamStatus: string | null;
    lifeCycleStatus: string | null;
    statusCheckedAt: number | null;
    healthCheckedAt: number | null;
  };
  earnings: {
    headline: {
      latency: 'monthly_actual';
      actualAdRevenueCents: number;
      finalizedMonthsCount: number;
      latestFinalizedPeriod: string | null;
      hasActuals: boolean;
    };
    forecast: {
      latency: 'estimated_delayed';
      estimatedAdRevenueCents: number;
      estimatedRevenueCents: number;
      latestReportDate: string | null;
      estimated: true;
    };
  };
  correlation: {
    pathAttribution: 'complete' | 'incomplete';
    totals: {
      legacyFlipsObserved: number;
      bridgeFlipsObserved: number;
      unattributedFlipsObserved: number;
    };
  };
};

const usd = (cents: number): string =>
  '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Relative "hace N min" from a server timestamp. The live plane is a periodic
// cron poll, NOT a push feed, so we surface the real freshness of the last
// snapshot instead of claiming "en tiempo real".
function ago(ts: number | null | undefined): string {
  if (!ts) return 'sin sondeo aún';
  const delta = Date.now() - ts;
  if (delta < 0) return 'recién';
  const s = Math.floor(delta / 1000);
  if (s < 45) return 'hace segundos';
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} d`;
}

// Compact "Actualizado hace N min" chip for a polled section header. Amber when
// the snapshot is stale/absent, muted when fresh — so an operator reads staleness
// at a glance without mistaking a slow poll for a live number.
function FreshnessChip({ ts, staleAfterMs }: { ts: number | null; staleAfterMs: number }) {
  const stale = ts == null || Date.now() - ts > staleAfterMs;
  return (
    <span
      className="t-mono"
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: stale ? 'var(--amber, #d97706)' : 'var(--muted-slate)',
      }}
    >
      Actualizado {ago(ts)}
    </span>
  );
}

export function YoutubeCorrelationCard({
  channelId,
  rangeDays = 30,
}: {
  channelId: string;
  rangeDays?: number;
}) {
  const data = useQuery(
    api.ytCorrelation.getYoutubeChannelCorrelation,
    { channelId: channelId as any, rangeDays },
  ) as Correlation | null | undefined;

  if (data === undefined) {
    return (
      <div>
        <PageHead title="Correlación de monetización (YouTube)" description={`Señal en vivo e ingresos — últimos ${rangeDays} días.`} />
        <SkeletonPanel h={160} />
        <div style={{ height: 24 }} />
        <SkeletonStatRow n={2} />
      </div>
    );
  }

  if (data === null) {
    return (
      <div>
        <PageHead title="Correlación de monetización (YouTube)" description={`Señal en vivo e ingresos — últimos ${rangeDays} días.`} />
        <Panel>
          <EmptyState
            icon="activity"
            title="Sin datos de correlación"
            body="Este canal todavía no tiene señal de YouTube (salud en vivo o ingresos) para el rango seleccionado."
          />
        </Panel>
      </div>
    );
  }

  const { liveViewers: live, earnings } = data;

  return (
    <div>
      <PageHead title="Correlación de monetización (YouTube)" description={`Señal en vivo e ingresos — últimos ${rangeDays} días.`} />

      {/* LIVE plane — a periodic cron poll, NOT a push feed, so it is grouped
          into one section stamped with its real freshness ("actualizado hace N
          min") rather than an "en tiempo real" claim the poll cadence can't
          honor. Stream status + viewer count share the `youtube-sync` stamp
          (~2 min prod); ingest health rides the slower `youtube-ingest-health`
          poll (~5 min prod) and shows its own stamp. */}
      <Panel
        title="Señal en vivo · sondeo periódico"
        reviewed="ok"
        right={<FreshnessChip ts={live.statusCheckedAt} staleAfterMs={12 * 60_000} />}
        style={{ marginBottom: 12 }}
      >
        <div className="circa-grid-4">
          <StatCard
            label="Estado en vivo"
            value={live.youtubeStreamStatus ?? '—'}
            hint={`sondeo · ${ago(live.statusCheckedAt)}`}
            icon="radio"
            tone={live.youtubeStreamStatus === 'live' ? 'good' : live.youtubeStreamStatus === 'error' ? 'bad' : undefined}
          />
          <StatCard
            label="Espectadores concurrentes"
            value={live.concurrentViewers != null ? live.concurrentViewers.toLocaleString('es-MX') : '—'}
            hint={`sondeo · ${ago(live.statusCheckedAt)}`}
            icon="users"
          />
          <StatCard
            label="Salud de ingesta"
            value={live.ingestHealth ?? <Provenance kind="absent" detail="El sondeo de salud de ingesta (cron youtube-ingest-health · Nango OAuth · liveStreams.list) aún no ha cubierto este canal — no hay fila de salud escrita todavía.">—</Provenance>}
            hint={live.healthCheckedAt ? `sondeo · ${ago(live.healthCheckedAt)}` : 'sin sondeo aún'}
            icon="gauge"
            tone={live.ingestHealth === 'good' ? 'good' : live.ingestHealth === 'bad' || live.ingestHealth === 'noData' ? 'bad' : undefined}
          />
        </div>
      </Panel>

      {/* REVENUE plane — two SEPARATE latency-tagged figures, never conflated. */}
      <div className="circa-grid-4">
        <StatCard
          label="Ingresos liquidados"
          value={earnings.headline.hasActuals
            ? usd(earnings.headline.actualAdRevenueCents)
            : <Provenance kind="absent" detail="ytRevenueMonthlyActual se llena desde un Worker externo (push a R2) que no está desplegado en este repo — el cron interno de actuals es un no-op. Sin cifra liquidada hasta ese despliegue.">—</Provenance>}
          hint={earnings.headline.latestFinalizedPeriod ? `actuales · ${earnings.headline.latestFinalizedPeriod}` : 'actuales mensuales'}
          icon="dollar"
          tone={earnings.headline.hasActuals ? 'good' : undefined}
        />
        <StatCard
          label="Pronóstico (estimado)"
          value={<Provenance kind="local" detail="Cifra estimada de la API de YouTube Reporting — se revisa a medida que YouTube consolida el reporte del día (retraso ~48–72 h). NO liquidada.">{usd(earnings.forecast.estimatedAdRevenueCents)}</Provenance>}
          hint="estimado · ~48–72 h"
          icon="dollar"
        />
      </div>
    </div>
  );
}
